// The Add Recipient dropdown's typed-prefix highlight (busy-my-web#199, Figma 40008595-226493).
//
// Step 2 of the frame filters the contact list by what has been typed and draws the MATCHED PREFIX
// of the name in Semi Bold — "**S**affet", "**S**arah" — leaving the rest Regular. Splitting the
// name is the whole of that rule, so it lives here as a function over two strings rather than as a
// v-html the row would have to build: the menus (To / Cc / Bcc / participants) all draw the same
// row, and a highlight each of them re-derives is a highlight that drifts.
//
// Only a PREFIX is emboldened. The list itself matches more loosely (a typed string anywhere in the
// name or the address still finds a contact), and a match in the middle of a word has no prefix to
// draw — so those rows come back whole, which is what the frame shows.

/**
 * `{ head, tail }` for one row: `head` is the part of `label` that `typed` matched at its start
 * (Semi Bold), `tail` is everything after it. An empty `head` means "draw the label as it is".
 */
export function prefixMatch (label, typed) {
  const name = String(label ?? '')
  const q = String(typed ?? '').trim()
  if (!q || !name.toLowerCase().startsWith(q.toLowerCase())) return { head: '', tail: name }
  return { head: name.slice(0, q.length), tail: name.slice(q.length) }
}

/**
 * One row per PERSON (busy-my-web#256).
 *
 * The chooser reads the contact book straight through, and the book can hold the same address
 * twice — a contact created from the box and then handed back by a later fetch, or simply saved
 * twice by hand — so a contact was offered twice in the Add Recipient list (and, since the rows are
 * keyed by the address, under a duplicate key). The address is what a recipient IS, so it is the
 * identity the list de-duplicates on: the first record carrying an address wins and every later one
 * is dropped, whatever else differs about it.
 *
 * Case- and space-insensitive, because "Sam@X.com" and "sam@x.com " are one mailbox. A record with
 * no address at all is dropped: there is nothing to send to, which is the same rule the callers'
 * own filter already applies.
 */
/**
 * The identity of a mailbox: trimmed and lower-cased, so "Sam@X.com" and "sam@x.com " are one
 * address. Lives here beside `dedupeByEmail` because the contact store guards on the same key
 * (busy-my-web#260) and two normalisations that can drift are two different contact books.
 */
export function normaliseEmail (value) {
  return String(value ?? '').trim().toLowerCase()
}

export function dedupeByEmail (contacts = []) {
  const seen = new Set()
  const out = []
  for (const contact of contacts || []) {
    const key = normaliseEmail(contact?.emailAddress)
    if (!key || seen.has(key)) continue
    seen.add(key)
    out.push(contact)
  }
  return out
}

/**
 * The suggestion list a row v-for reads: each contact with its name already split. `contact` is the
 * untouched record, so picking a row still hands the caller exactly what the store holds.
 */
export function suggestionRows (suggestions = [], typed = '') {
  return (suggestions || []).map(contact => ({
    contact,
    ...prefixMatch(contact?.name || contact?.emailAddress || '', typed),
  }))
}

export default suggestionRows
